import constants from './constants'
import moment from 'moment-timezone'

/**
 * Helperクラス
 */
class YokumiruHelper {
    /**
     * 会員タイムゾーンに変換
     *
     * val(string): Asia/Tokyoに変換→引数のtimezoneに変換
     * val(moment): valのタイムゾーン→Asia/Tokyoに変換→引数のtimezoneに変換（つまり、valのタイムゾーンと引数のtimezoneが同じ場合は変化なし）
     *
     * @param {string|moment} val
     * @param {string} timezone
     * @return {moment}
     */
    static userTime (val, timezone) {
        const jstMoment = moment.tz(val, 'Asia/Tokyo')
        const jstOffset = jstMoment.utcOffset()

        const userMoment = moment.tz(val, 'Asia/Tokyo').tz(timezone)
        const userOffset = userMoment.utcOffset()

        const diff = Math.abs(jstOffset - userOffset)
        const isGreaterThanJST = userOffset > jstOffset

        if (isGreaterThanJST) {
            return jstMoment.add(diff, 'minutes')
        } else {
            return jstMoment.subtract(diff, 'minutes')
        }
    }

    /**
     * 会員タイムゾーンの現在日時
     * @param timezone
     * @return {moment}
     */
    static userTimeNow (timezone) {
        const jstMoment = moment.tz('Asia/Tokyo')
        const jstOffset = jstMoment.utcOffset()

        const userMoment = moment.tz('Asia/Tokyo').tz(timezone)
        const userOffset = userMoment.utcOffset()

        const diff = Math.abs(jstOffset - userOffset)
        const isGreaterThanJST = userOffset > jstOffset

        if (isGreaterThanJST) {
            return jstMoment.add(diff, 'minutes')
        } else {
            return jstMoment.subtract(diff, 'minutes')
        }
    }

    /**
     * 指定された日時とタイムゾーンの組み合わせがサマータイムかどうか
     * @param {string|moment} val
     * @param {string} timezone
     * @return {boolean}
     */
    static isUserSummerTime (val, timezone) {
        return moment.tz(val, 'Asia/Tokyo').tz(timezone).isDST()
    }

    /**
     * 会員の現在日時のタイムゾーンがサマータイムかどうか
     * @param timezone
     * @return {boolean}
     */
    static isUserSummerTimeNow (timezone) {
        return moment.tz('Asia/Tokyo').tz(timezone).isDST()
    }

    /**
     * JSTタイムゾーンに変換
     * @param {string|moment} val
     * @param {string} timezone
     * @return {moment}
     */
    static jstTime (val, timezone) {
        const jstMoment = moment.tz(val, 'Asia/Tokyo')
        const jstOffset = jstMoment.utcOffset()

        const userMoment = moment.tz(val, timezone)
        const userOffset = userMoment.utcOffset()

        const diff = Math.abs(jstOffset - userOffset)
        const isGreaterThanJST = userOffset > jstOffset

        if (isGreaterThanJST) {
            return jstMoment.subtract(diff, 'minutes')
        } else {
            return jstMoment.add(diff, 'minutes')
        }
    }

    /**
     * JSTタイムゾーンの現在日時
     * @return {moment}
     */
    static jstTimeNow () {
        return moment.tz('Asia/Tokyo')
    }

    /**
     * 日付表記フォーマット（timezone変換なし）
     * @param {string|moment} val
     * @return {string}
     */
    static dateForHumanNoConvert(val) {
        return moment(val).format('YYYY年M月D日')
    }

    /**
     * 日付表記フォーマット（timezone変換なし）
     * @param {string|moment} val
     * @param options
     * @return {string}
     */
    static dayForHumanNoConvert(val, options = {}) {
        const date = moment(val)
        const day = constants.days[date.day()]

        if (options.hasOwnProperty('bracket') && options.bracket === true) {
            return `（${day}）`
        }

        return day
    }

    /**
     * 曜日表記CSS（timezone変換なし）
     * @param {string|moment} val
     * @return {string}
     */
    static dayForHumanNoConvertCSS(val) {
        const date = moment(val)
        const day = date.day()

        if (day === 0) return 'text-danger'
        if (day === 6) return 'text-yokumiru-blue'

        return ''
    }

    /**
     * 相談時間表記（timezone変換なし）
     * @param {string} startTime
     * @param {string} endTime
     * @return {string}
     */
    static displayTimeNoConvert(startTime, endTime) {
        let start = moment(`1970-01-01 ${startTime}`).format('ah:mm')
        let end = moment(`1970-01-01 ${endTime}`).format('ah:mm')

        if (start.includes('午前12')) {
            start = start.replace('午前12', '午前0')
        }
        if (end.includes('午前12')) {
            end = end.replace('午前12', '午前0')
        }

        return `${start} - ${end}`
    }

    /**
     * 開始時間表記（timezone変換なし）
     * @param {string} startTime
     * @return {string}
     */
    static displayStartTimeNoConvert(startTime) {
        const time = moment(`1970-01-01 ${startTime}`).format('ah:mm〜')

        return time.includes('午前12') ? time.replace('午前12', '午前0') : time
    }

    /**
     * 日付表記フォーマット
     * @param {string|moment} val
     * @param {string} timezone
     * @param {object} options
     * @return {string}
     */
    static dateForHuman(val, timezone, options = {}) {
        let userTime = this.userTime(val, timezone)

        if (options.hasOwnProperty('exceptYear') && options.exceptYear === true) {
            return userTime.format('M月D日')
        }

        if (options.hasOwnProperty('simpleYear') && options.simpleYear === true) {
            return userTime.format('YY年M月D日')
        }

        return userTime.format('YYYY年M月D日')
    }

    /**
     * 曜日表記フォーマット
     * @param {string|moment} val
     * @param {string} timezone
     * @param {object} options
     * @return {string}
     */
    static dayForHuman(val, timezone, options = {}) {
        let userTime = this.userTime(val, timezone)
        const day = constants.days[userTime.day()]

        if (options.hasOwnProperty('bracket') && options.bracket === true) {
            return `（${day}）`
        }

        return day
    }

    /**
     * 曜日表記CSS
     * @param {string|moment} val
     * @param {string} timezone
     * @return {string}
     */
    static dayForHumanCSS(val, timezone) {
        let userTime = this.userTime(val, timezone)
        const day = userTime.day()

        if (day === 0) return 'text-danger'
        if (day === 6) return 'text-yokumiru-blue'

        return ''
    }

    /**
     * 時間表記
     * @param {string|object} val
     * @param {string} timezone
     * @return {string}
     */
    static timeForHuman(val, timezone) {
        let start, end

        if (typeof val === 'string') {
            // val = 日時文字列のとき
            start = this.userTime(val, timezone).format('ah:mm')
            return start.includes('午前12') ? start.replace('午前12', '午前0') : start
        }

        if (val.hasOwnProperty('doctor_menu') && val.doctor_menu) {
            // val = Consultation のとき
            start = this.userTime(val.start_time, timezone)
            end = start.clone().add(val.doctor_menu.consultation_minutes, 'minutes')
        }
        else if (val.start_time.hasOwnProperty('date')) {
            start = this.userTime(val.start_time.date, timezone)
            end = this.userTime(val.end_time.date, timezone)
        }
        else {
            start = this.userTime(val.start_time, timezone)
            end = this.userTime(val.end_time, timezone)
        }

        start = start.format('ah:mm')
        end = end.format('ah:mm')

        if (start.includes('午前12')) {
            start = start.replace('午前12', '午前0')
        }
        if (end.includes('午前12')) {
            end = end.replace('午前12', '午前0')
        }

        return `${start} - ${end}`
    }

    /**
     * 日付表示フォーマット
     * @param {string|moment} datetime
     * @param {string} timezone
     * @returns {Object} moment
     */
    static dateTimeForHuman(datetime, timezone) {
        const time = this.userTime(datetime , timezone).format('YYYY年M月D日 ah:mm')

        return time.includes('午前12') ? time.replace('午前12', '午前0') : time
    }

    /**
     * 生年月日フォーマット
     *
     * @param {string} birthday
     * @return {string}
     */
    static birthdayForHuman(birthday) {
        return moment(birthday).format('YYYY年M月D日')
    }

    /**
     * 現在の西暦と一致するかどうか
     *
     * @param {string} datetime
     * @param {string} timezone
     *
     * @return {boolean}
     */
    static isCurrentYear(datetime, timezone) {
        return this.userTime(datetime, timezone).year() === this.userTimeNow(timezone).year()
    }

    /**
     * JSTに対するオフセット取得
     *
     * @deprecated getOffsetFromUTCを使用すること
     *
     * @param {string} timezone
     * @return {string|number}
     */
    static getOffsetFromJapan(timezone) {
        const japan = moment().tz("Asia/Tokyo").format()
        const user = moment().tz(timezone).format()

        const returnValue = (moment.parseZone(user).utcOffset() - moment.parseZone(japan).utcOffset()) / 60

        if (returnValue > 0) {
            return '+' + returnValue
        }

        return returnValue
    }

    /**
     * UTCオフセットの取得
     *
     * @param {string} timezone
     * @return {string}
     */
    static getOffsetFromUTC(timezone) {
        const userTimezone = moment().tz(timezone).format();
        const offset = moment.parseZone(userTimezone).utcOffset()
        const hours = Math.abs(parseInt(offset / 60)).toString()
        const minutes = Math.abs(parseInt(offset % 60)).toString()

        if (timezone === 'UTC') {
            return ''
        }

        const format = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`

        if (offset === 0) {
            return `(UTC+${format})`
        }
        else {
            return offset > 0 ? `(UTC+${format})` : `(UTC-${format})`
        }
    }

    /**
     * ミリ秒を分:秒表記に変換する
     * @param {*} milliseconds 
     * @returns 
     */
    static millisToMinutesAndSeconds (milliseconds) {
        var duration = moment.duration(milliseconds);
        var seconds = duration.seconds();
        seconds = (seconds < 10) ? "0" + seconds : seconds;
      
        return duration.minutes() + ":" + seconds;
      }

    /**
     * 複数人予約の順番
     *
     * @param {object} target
     * @param {array} consultations
     *
     * @return {string}
     */
    static consultationOrder(target, consultations) {
        if (target.flg_cancel) return ''

        const cons = consultations.filter(consultation => {
            return consultation.start_time === target.start_time
                && consultation.id !== target.id
                && !consultation.flg_cancel
        })
        if (cons.length < 1) return ''

        const order = cons.filter(consultation => target.id > consultation.id).length + 1
        return `[${order}]`
    }

    /**
     * 設定値をタイプ毎にバインド
     *
     * @param {array} configurations
     * @return {array}
     */
    static bindConfigurationsValue(configurations) {
        const result = configurations.map(conf => {
            if (conf.value_type === constants.confValue.int) {
                conf.value_int = conf.value
            }
            else if (conf.value_type === constants.confValue.str) {
                conf.value_str = conf.value
            }
            else if (conf.value_type === constants.confValue.text) {
                conf.value_text = conf.value
            }
            else if (conf.value_type === constants.confValue.image) {
                return null
            }
            else if (conf.value_type === constants.confValue.bool) {
                conf.value_bool = conf.value
            }
            else if (conf.value_type === constants.confValue.datetime) {
                conf.value_datetime = conf.value
            }
            return conf
        })
        return result.filter(conf => conf !== null)
    }

    /**
     * 設定値のタイプ毎にフィールド名を返す
     *
     * @param {object} conf
     * @return {string}
     */
    static valueField(conf) {
        if (conf.value_type === constants.confValue.int) {
            return 'value_int'
        }
        else if (conf.value_type === constants.confValue.str) {
            return 'value_str'
        }
        else if (conf.value_type === constants.confValue.text) {
            return 'value_text'
        }
        else if (conf.value_type === constants.confValue.image) {
            return 'value_image'
        }
        else if (conf.value_type === constants.confValue.bool) {
            return 'value_bool'
        }
        else if (conf.value_type === constants.confValue.datetime) {
            return 'value_datetime'
        }
        return ''
    }

    /**
     * 税込料金
     *
     * @param {number} price
     * @return {number}
     */
    static inTax(price) {
        return Math.floor(price * 1.1)
    }

    /**
     * 再相談可能かどうか
     * @see \App\Models\DoctorMenu::isRepeatable() ※同じロジックで判定しています
     *
     * @param {object} doctorMenu
     * @param {array[object]} consultations
     * @param {string} timezone
     */
    static isRepeatable(doctorMenu, consultations = [], timezone = 'Asia/Tokyo') {
        if ( ! doctorMenu.is_repeatable ) return false

        return consultations.some((consultation) => {
            // メニューと同じ相談科・医師の相談予約かどうか
            const isSame = consultation.medical_course_id === doctorMenu.medical_course_id
                && consultation.doctor_id === doctorMenu.doctor_id

            if (!isSame) return false

            // 前回の相談から再相談可能な日数に収まっているかどうか
            const days = doctorMenu.repeat_reservable_days || 0
            const date = this.userTime(consultation.end_time, timezone).add(days, 'days').endOf('day')
            const now = this.userTimeNow(timezone)

            return now.isBefore(date)
        })
    }

    /**
     * 課金対象が個人かどうか
     * @param {object} user
     * @return {boolean}
     */
    static isBillingPersonal(user) {
        return user 
            && user.plan
            && user.plan.billing_target === constants.billingTarget.personal
    }

    /**
     * 支払い方法選択をスキップする場合のデフォルト値
     * @param {object} user
     * @return {object}
     */
    static confirmPaymentMethod(user) {
        if (this.isBillingPersonal(user) && !this.isCompanyLegacy(user)) {
            return {
                id: constants.payment_methods.credit,
                name: 'クレジットカード',
            }
        }
        return {
            id: constants.payment_methods.invoiceLater,
            name: '請求書後払い',
        }
    }

    /**
     * サービスモデル: 一般会員かどうか
     * @param {object} plan
     * @return {boolean}
     */
    static isDefaultPlan(plan) {
        if (!plan) return false
        return plan.key === constants.plans.payPer
    }

    /**
     * サービスモデル: 定額プランかどうか
     *
     * @param {object} plan
     * @return {boolean}
     */
    static isFlatRatePlan(plan) {
        if (!plan) return false
        return plan.key.includes(constants.plans.flatRate)
    }

    /**
     * サービスモデル: 海外ドクターパスかどうか
     *
     * @param {object} plan
     * @return {boolean}
     */
    static isTravelPlan(plan) {
        if (!plan) return false
        return plan.key.includes(constants.plans.travel)
    }

    /**
     * サービスモデル: 留学ドクターパスかどうか
     *
     * @param {object} plan
     * @return {boolean}
     */
    static isStudyAbroadPlan(plan) {
        if (!plan) return false
        return plan.key.includes(constants.plans.studyAbroad)
    }

    /**
     * サービスモデル: ドクターパスかどうか
     *
     * @param {object} plan
     * @return {boolean}
     */
    static isDoctorPass(plan) {
        if (!plan) return false
        return this.isTravelPlan(plan) || this.isStudyAbroadPlan(plan)
    }

    /**
     * サービスモデル: 買い切りのプランかどうか
     *
     * @param {object} plan
     * @return {boolean}
     */
    static isPurchasePlan(plan) {
        if (!plan) return false
        return [constants.planTypes.purchase].includes(plan.plan_type)
    }

    /**
     * サービスモデル: 請求サイクルを持つプランかどうか
     *
     * @param {object} plan
     * @return {boolean}
     */
    static isCyclePlan(plan) {
        if (!plan) return false
        return [constants.planTypes.cycle].includes(plan.plan_type)
    }

    /**
     * 利用中ステータス判定
     *
     * @param {object} subscription
     * @return boolean
     */
    static isStatusActive(subscription)
    {
        if (!subscription) return false
        return subscription.status === constants.subscriptions.active
    }

    /**
     * 利用開始前ステータス判定
     *
     * @param {object} subscription
     * @return boolean
     */
    static isStatusNotStarted(subscription)
    {
        if (!subscription) return false
        return subscription.status === constants.subscriptions.notStarted
    }

    /**
     * サブスクリプション所有判定
     * @see User::hasSubscription()
     *
     * @param {object} user
     * @param {boolean} isStrict 利用期間内か厳密にチェックするフラグ
     * @param {array} additionalStatuses 対象とする追加ステータス
     * @return {boolean}
     */
    static hasSubscription(user, isStrict = false, additionalStatuses = []) {
        if (!user || !user.subscription || !this.isDoctorPass(user.plan)) {
            return false
        }

        if (!isStrict) {
            return true
        }

        let statuses = constants.subscriptions.enableAll
        if (additionalStatuses.length > 0) {
            statuses = statuses.concat(additionalStatuses)
        }

        // 会員のタイムゾーン基準で考える必要があるなら以下
        // const now = this.userTimeNow(user.timezone)
        // const start = this.userTime(user.subscription.period_start, user.timezone)
        // const end = this.userTime(user.subscription.period_end, user.timezone)

        // 日本時間基準で考えてOKなら以下
        const now = moment()
        const start = moment(user.subscription.period_start)
        const end = user.subscription.period_end ? moment(user.subscription.period_end) : moment()

        return statuses.includes(user.subscription.status)
            && now.isSameOrAfter(start)
            && now.isSameOrBefore(end)
    }

    /**
     * 利用終了日時が設定されているかどうか
     * （留学ドクターパスの場合は申込時に帰国日を設定しないため period_end は null）
     *
     * @param user
     * @return {boolean}
     */
    static hasSubscriptionPeriodEnd(user) {
        return this.hasSubscription(user, false, [constants.subscriptions.canceled]) && !!user.subscription.period_end
    }

    /**
     * 指定日時がサブスクリプションの利用期間内かどうか
     *
     * @param {object} user
     * @param {string|moment} datetime
     * @return {boolean}
     */
    static isSubscriptionPeriod(user, datetime) {
        // 会員のタイムゾーン基準で考える必要があるなら以下
        // if (typeof datetime === 'string') datetime = this.userTime(datetime, user.timezone)
        // const start = this.userTime(user.subscription.period_start, user.timezone)
        // const end = this.userTime(user.subscription.period_end, user.timezone)

        // 日本時間基準で考えてOKなら以下
        if (typeof datetime === 'string') datetime = moment(datetime)
        const start = moment(user.subscription.period_start)
        const end = user.subscription.period_end ? moment(user.subscription.period_end) : moment()

        return datetime.isSameOrAfter(start) && datetime.isBefore(end)
    }

    /**
     * サブスクリプションの利用期間（開始）を返す
     *
     * @param {object} user
     * @param {boolean} inUserTimezone
     * @return {false|moment}
     */
    static subscriptionPeriodStart(user, inUserTimezone = false) {
        // 会員のタイムゾーン基準
        if (inUserTimezone) {
            return this.userTime(user.subscription.period_start, user.timezone)
        }
        // 日本時間基準
        return moment(user.subscription.period_start)
    }

    /**
     * サブスクリプションの利用期間（終了）を返す
     *
     * @param {object} user
     * @param {boolean} inUserTimezone
     * @return {false|moment}
     */
    static subscriptionPeriodEnd(user, inUserTimezone = false) {
        // 会員のタイムゾーン基準
        if (inUserTimezone) {
            return this.userTime(user.subscription.period_end, user.timezone)
        }
        // 日本時間基準
        return user.subscription.period_end ? moment(user.subscription.period_end) : moment()
    }

    /**
     * サブスクリプション利用終了までの日数を返す（日単位）
     * 24hで1日と見なすので、当日分は含まれないため注意
     *
     * @param {object} user
     * @param {boolean} inUserTimezone
     * @return {number}
     */
    static subscriptionDiffDays(user, inUserTimezone = false) {
        // 会員のタイムゾーン基準
        if (inUserTimezone) {
            const today = this.userTimeNow(user.timezone).startOf('days')
            const endDay = this.subscriptionPeriodEnd(user, true).startOf('days')
            return endDay.diff(today, 'days')
        }
        // 日本時間基準
        const today = moment().startOf('days')
        const endDay = this.subscriptionPeriodEnd(user).startOf('days')
        return endDay.diff(today, 'days')
    }

    /**
     * 法人判定
     *
     * @param {object} group
     * @return {boolean}
     */
    static isCompany(group) {
        return Number(group.group_type) === constants.groupType.company
    }

    /**
     * 団体判定
     *
     * @param {object} group
     * @return {boolean}
     */
    static isOrganization(group) {
        return Number(group.group_type) === constants.groupType.organization
    }

    /**
     * グループ表記
     *
     * @param {number|string} groupType
     * @return {string}
     */
    static groupTypeName(groupType) {
        if (Number(groupType) === constants.groupType.organization) return '団体'
        if (Number(groupType) === constants.groupType.company) return '法人'
        return ''
    }

    /**
     * グループモデル
     *
     * @param {number|string} groupType
     * @param {boolean} isPlural
     * @return {string}
     */
    static groupTypeModel(groupType, isPlural = false) {
        if (Number(groupType) === constants.groupType.organization) return isPlural ? 'organizations' : 'organization'
        if (Number(groupType) === constants.groupType.company) return isPlural ? 'companies'  : 'company'
        return ''
    }

    /**
     * 会員の所属法人を返す
     * @param {object} user
     * @return {object|undefined}
     */
    static userCompany(user) {
        if (!user) return null

        return user.groups.find((group) => {
            return Number(group.group_type) === constants.groupType.company
        })
    }

    /**
     * 会員の所属団体を返す
     *
     * @param {object} user
     * @return {object|undefined}
     */
    static userOrganization(user) {
        if (!user) return null

        return user.groups.find((group) => {
            return Number(group.group_type) === constants.groupType.organization
        })
    }

    /**
     * グループが所属する団体を返す
     *
     * @param {object} group
     * @return {object|null}
     */
    static parentOrganization(group) {
        if (!group || !group.parent_groups) return null

        return group.parent_groups.find((group) => {
            return group.group_type === constants.groupType.organization
        })
    }

    /**
     * 会員がグループに所属しているかどうか
     * @param {object} user
     * @return {boolean}
     */
    static isGroupUser(user) {
        if (!user) return false

        return user.is_organization || user.is_company
    }

    /**
     * 会員が法人に所属しているかどうか（従量課金 + 団体所属していない旧タイプ）
     * @param {object} user
     * @return {boolean}
     */
    static isCompanyLegacy(user) {
        if (!user) return false

        return this.isDefaultPlan(user.plan)
            && !user.is_organization
            && user.is_company
    }

    /**
     * 会員のグループを返す（団体・法人の両方に所属している場合、団体 > 法人 の優先度で返す）
     * @param {object} user
     * @return {object|null}
     */
    static currentGroup(user) {
        const organization = this.userOrganization(user)
        if (organization) {
            return organization
        }
        const company = this.userCompany(user)
        if (company) {
            return company
        }
        return null
    }

    /**
     * 会員が所属するグループの利用可能なプランの一覧を取得
     *
     * @param {object} user
     * @return {array}
     */
    static retrieveUserGroupPlans(user) {
        const group = this.currentGroup(user)
        return group ? group.plans : []
    }

    /**
     * 所持クーポンのフィルタリング（相談予約カテゴリのクーポンだけにする）
     *
     * @param {object} user
     * @return {array}
     */
    static filteredUserCoupons(user) {
        if (this.isGroupUser(user)) {
            return []
        }
        return user.user_coupons.filter((uc) => uc.category === constants.couponCategory.consultation)
    }

    /**
     * 最大割引となる所持クーポンを返す
     * @param {array} userCoupons
     * @param {object} doctorMenu
     * @param {object} consultation
     * @return {object|null}
     */
    static maxDiscountCoupon(userCoupons, doctorMenu, consultation = null) {
        // 無料メニューの場合は割引表示しない
        if (!doctorMenu || doctorMenu.consultation_price < 1) {
            return null
        }

        // 予約データがある場合、予約時に使用されたクーポンを優先的に返す
        if (consultation && consultation.user_coupon) {
            return consultation.user_coupon
        }

        let result = null

        userCoupons.forEach((userCoupon) => {
            if (userCoupon.is_only_matching) {
                return
            }
            if (userCoupon.min_consultation_price) {
                if (doctorMenu.consultation_price < userCoupon.min_consultation_price) {
                    return
                }
            }
            if (userCoupon.has_doctor_menu) {
                if (userCoupon.doctor_menus.every((dm) => dm.id !== doctorMenu.id)) {
                    return
                }
            }
            if (userCoupon.has_doctor) {
                if (userCoupon.doctors.every((doctor) => doctor.id !== doctorMenu.doctor_id)) {
                    return
                }
            }

            if (result === null || userCoupon.discount > result.discount) {
                result = userCoupon
            }
        })

        return result
    }

    static maxMatchingDiscountCoupon(userCoupons, user, medicalCourse) {
        const matchingPrice = medicalCourse.matching_price

        // 無料マッチングの場合は割引表示しない
        if (matchingPrice < 1) {
            return null
        }

        // 相談料の決済が不要なら割引表示しない
        if (!user.plan.is_pay_consultation_price) {
            return null
        }

        let result = null

        userCoupons.forEach((userCoupon) => {
            if (userCoupon.min_consultation_price) {
                if (matchingPrice < userCoupon.min_consultation_price) {
                    return
                }
            }
            if (userCoupon.has_doctor_menu) {
                return
            }
            if (userCoupon.has_doctor) {
                return
            }

            if (result === null || userCoupon.discount > result.discount) {
                result = userCoupon
            }
        })

        return result
    }

    /**
     * マッチング予約用のメニュー情報表示
     * @return {object}
     */
    static matchingDoctorMenu(medicalCourse) {
        return {
            id: null,
            doctor_id: null,
            medical_course_id: medicalCourse.id,
            medical_course: {
                name: medicalCourse.name
            },
            title: `${medicalCourse.name}メニュー`,
            consultation_minutes: medicalCourse.matching_minutes,
            consultation_price: medicalCourse.matching_price,
            is_health_package: false,
            repeat_reservable_days: 0,
            repeat_consultation_price: null,
            description: '選択した日程・時間帯に合わせて、あなたのお悩みに最適な医師をヨクミルがご提案します',
            doctor_menu_labels: [],
        }
    }

    /**
     * マッチング予約用のメニュー情報表示（description）
     * @return {object}
     */
    static matchingDoctorMenuDesc() {
        return '選択した日程・時間帯に合わせて、あなたのお悩みに最適な医師をヨクミルがご提案します'
    }

    /**
     * ドラフト予約かどうか
     * @param {object} consultation
     * @return {boolean}
     */
    static isDraft(consultation) {
        if (!consultation) return false

        return consultation.is_draft
            && consultation.flg_cancel
    }

    /**
     * 医師によるドラフト予約かどうか
     * @param {object} consultation
     * @return {boolean}
     */
    static isDraftByDoctor(consultation) {
        if (!consultation) return false

        return consultation.is_draft
            && consultation.flg_reserved_by_doctor
            && !consultation.flg_cancel
    }
}

export default YokumiruHelper
